import { useCallback, useMemo } from "react";
import type { SavedViewScope } from "services/saved-view-api";
import {
  useCreateSavedViewMutation,
  useDeleteSavedViewMutation,
  useGetSavedViewsQuery,
  useUpdateSavedViewMutation,
} from "services/saved-view-api";
import { useToken } from "utils/context.ts";

interface SavePresetParams {
  name: string;
  description?: string;
  scope?: SavedViewScope;
  shareableUrl: string;
}

/* hooks for saved views, ie. preset saving, updating, deleting */
export const useSavedViews = () => {
  const { accessToken } = useToken();

  // fetch saved views for current user
  const {
    data: savedViewsData,
    isLoading: isLoadingSavedViews,
    error: savedViewsError,
    refetch: refetchSavedViews,
  } = useGetSavedViewsQuery({}, { skip: !accessToken });

  // mutations
  const [createSavedView, { isLoading: isCreating }] =
    useCreateSavedViewMutation();
  const [updateSavedView, { isLoading: isUpdating }] =
    useUpdateSavedViewMutation();
  const [deleteSavedView, { isLoading: isDeleting }] =
    useDeleteSavedViewMutation();

  // get saved views specific to finance reporting
  const financePresets = useMemo(() => {
    if (!savedViewsData?.results) return [];

    return savedViewsData.results.filter(
      (view) =>
        view.url_path.includes("/reporting/all-reports") ||
        view.url_path.includes("reportType="),
    );
  }, [savedViewsData]);

  // save current filters as a preset
  const saveAsPreset = useCallback(
    async ({
      name,
      description,
      scope = "USER",
      shareableUrl,
    }: SavePresetParams) => {
      if (!accessToken) {
        throw new Error("Authentication required");
      }

      try {
        // extract just the path and query string from the full URL
        const url = new URL(shareableUrl);
        const urlPath = url.pathname + url.search;

        const result = await createSavedView({
          data: {
            name,
            url_path: urlPath,
            scope,
            description: description,
          },
        }).unwrap();

        return result;
      } catch (error) {
        console.error("Failed to save preset:", error);
        throw error;
      }
    },
    [accessToken, createSavedView],
  );

  // update existing preset
  const updatePreset = useCallback(
    async (
      id: number,
      updates: {
        name?: string;
        description?: string;
        scope?: SavedViewScope;
        shareableUrl?: string;
      },
    ) => {
      if (!accessToken) {
        throw new Error("Authentication required");
      }

      try {
        const updateData = {
          ...(updates.name && { name: updates.name }),
          ...(updates.description && { description: updates.description }),
          ...(updates.scope && { scope: updates.scope }),
          ...(updates.shareableUrl && {
            url_path: (() => {
              const url = new URL(updates.shareableUrl);
              return url.pathname + url.search;
            })(),
          }),
        };

        const result = await updateSavedView({
          id,
          data: updateData,
        }).unwrap();

        return result;
      } catch (error) {
        console.error("Failed to update preset:", error);
        throw error;
      }
    },
    [accessToken, updateSavedView],
  );

  const deletePreset = useCallback(
    async (id: number) => {
      if (!accessToken) {
        throw new Error("Authentication required");
      }

      try {
        await deleteSavedView({
          id,
        }).unwrap();
      } catch (error) {
        console.error("Failed to delete preset:", error);
        throw error;
      }
    },
    [accessToken, deleteSavedView],
  );

  // extract preset info from URL if loading from preset
  const getPresetFromUrl = useCallback(
    (searchParams: URLSearchParams) => {
      const presetId = searchParams.get("preset");
      if (!presetId) return null;

      const preset = financePresets.find(
        (p) => p.id === parseInt(presetId, 10),
      );
      return preset || null;
    },
    [financePresets],
  );

  return {
    // data
    savedViews: savedViewsData?.results || [],
    financePresets,

    // loading states
    isLoadingSavedViews,
    isCreating,
    isUpdating,
    isDeleting,

    // errors
    savedViewsError,

    // actions
    saveAsPreset,
    updatePreset,
    deletePreset,
    refetchSavedViews,

    // utilities
    getPresetFromUrl,
  };
};
